import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

/** Options for {@link useRetainedState}. */
export interface RetainedStateOptions {
  /**
   * Where the value lives beyond the current render tree. `"memory"`
   * (default) survives route changes only; `"local"` additionally
   * survives reloads via localStorage — values must round-trip
   * through JSON.
   */
  storage?: "memory" | "local";
}

/**
 * Key/value registry backing {@link useRetainedState}, keyed
 * `scope:key`. Module-level so values outlive route changes; a full
 * page reload resets it by design.
 */
const retained = new Map<string, unknown>();

/** localStorage key prefix for `storage: "local"` entries. */
const STORAGE_PREFIX = "retained:";

/**
 * Test-only: empties the registry (and any localStorage-persisted
 * entries) so tests stay hermetic. Wired into
 * `vitest.setup.shared.ts`'s `afterEach` via `@appretio/shared/tests`.
 */
export function clearRetainedState(): void {
  retained.clear();
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) window.localStorage.removeItem(key);
    }
  } catch {
    // No usable localStorage — nothing was persisted.
  }
}

function readStored<T>(id: string): T | undefined {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${id}`);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    // Unavailable storage (private-mode Safari) or a corrupted entry.
    return undefined;
  }
}

function writeStored(id: string, value: unknown): void {
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${id}`,
      JSON.stringify(value)
    );
  } catch {
    // Best effort — quota or private mode; in-memory retention still works.
  }
}

/**
 * Drop-in `useState` replacement whose value survives unmount/remount
 * within the browser session — and, with `storage: "local"`, across
 * reloads too.
 *
 * State is namespaced by `scope` + `key`: hooks using the same pair
 * see the last-written value on mount, so pages that share a scope
 * share their selections. Not reactive across simultaneously mounted
 * consumers — intended for page-level selection state where at most
 * one owner of a key is mounted at a time.
 *
 * Without a `scope` it is plain `useState`: nothing is retained, as with
 * {@link useRetainedRef}. For a hook whose caller decides whether its state
 * outlives the mount.
 */
export function useRetainedState<T>(
  scope: string | undefined,
  key: string,
  initialValue: T,
  { storage = "memory" }: RetainedStateOptions = {}
): [T, Dispatch<SetStateAction<T>>] {
  const id = scope === undefined ? undefined : `${scope}:${key}`;
  const [value, setValue] = useState<T>(() => {
    if (id === undefined) return initialValue;
    if (retained.has(id)) return retained.get(id) as T;
    if (storage === "local") {
      const stored = readStored<T>(id);
      if (stored !== undefined) return stored;
    }
    return initialValue;
  });

  useEffect(() => {
    if (id === undefined) return;
    retained.set(id, value);
    if (storage === "local") writeStored(id, value);
  }, [id, value, storage]);

  return [value, setValue];
}

/**
 * Ref-flavoured counterpart of {@link useRetainedState}: a stable
 * `{ current }` handle backed by the same registry, for values written
 * from high-frequency non-React events (AG Grid state callbacks). Reads
 * are fine during render — they write nothing — while writes never trigger
 * one, so a render that reads the value must be caused by something else.
 * Writes go straight to the registry, so nothing is lost if the owner
 * unmounts right after one. Memory-only.
 *
 * Unlike {@link useRetainedState}, two handles on one `scope`/`key` share
 * a slot live: each sees the other's writes. The pair is read once, when
 * the handle is created — a caller whose scope can change mid-mount would
 * keep writing to the old slot. Without a `scope` the handle is a plain ref
 * that dies with its owner: nothing is retained.
 */
export function useRetainedRef<T>(
  scope: string | undefined,
  key: string,
  initialValue: T
): { current: T } {
  const [handle] = useState(() => {
    if (scope === undefined) return { current: initialValue };
    const id = `${scope}:${key}`;
    // Seeded here, not on the first read, so reading during render writes
    // nothing — and so a value mutated in place is the registry's own.
    if (!retained.has(id)) retained.set(id, initialValue);
    return {
      get current(): T {
        return retained.get(id) as T;
      },
      set current(next: T) {
        retained.set(id, next);
      }
    };
  });
  return handle;
}
